import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';


import InviteUserModal from './InviteUserModal';
import InvitationListModal from './InvitationListModal';
import { tenantService } from '../../../services/tenantService';
import { useAuth } from '../../../contexts/AuthContext';
import { AdminGate } from '../../../components/ui/PermissionGate';

const TeamTab = () => {
  const { profile, isSuperAdmin, isBusinessAdmin } = useAuth();
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isInvitationListOpen, setIsInvitationListOpen] = useState(false);

  useEffect(() => {
    loadTeamMembers();
  }, [profile?.tenant_id]);

  const loadTeamMembers = async () => {
    try {
      setLoading(true);
      setError('');
      if (profile?.tenant_id) {
        const data = await tenantService?.getTenantUsers(profile?.tenant_id);
        setTeamMembers(data || []);
      }
    } catch (err) {
      setError(err?.message || 'Failed to load team members');
    } finally {
      setLoading(false);
    }
  };

  const handleInviteSuccess = () => {
    loadTeamMembers();
  };

  return (
    <div className="space-y-6">
      {/* Header with Invite Button */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Team Members</h3>
          <p className="text-sm text-gray-500 mt-1">
            Manage your team members and their roles
          </p>
        </div>
        <AdminGate tenantId={profile?.tenant_id}>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setIsInvitationListOpen(true)}
            >
              <Icon name="Mail" size={16} className="mr-2" />
              View Invitations
            </Button>
            <Button onClick={() => setIsInviteModalOpen(true)}>
              <Icon name="UserPlus" size={16} className="mr-2" />
              Invite Member
            </Button>
          </div>
        </AdminGate>
      </div>
      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
        </div>
      )}
      {/* Error State */}
      {error && (
        <div className="p-4 bg-error/10 border border-error/20 rounded-lg text-error">
          {error}
        </div>
      )}
      {/* Team Members List */}
      {!loading && !error && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Member
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Job Title
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Login
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {teamMembers?.map((member) => (
                  <tr key={member?.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          {member?.avatar_url ? (
                            <Image
                              src={member?.avatar_url}
                              alt={`${member?.first_name} ${member?.last_name}`}
                              className="h-10 w-10 rounded-full"
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                              <span className="text-primary font-medium">
                                {member?.first_name?.[0]}{member?.last_name?.[0]}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {member?.first_name} {member?.last_name}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{member?.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{member?.job_title || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        member?.is_active
                          ? 'bg-success/10 text-success' :'bg-gray-100 text-gray-600'
                      }`}>
                        {member?.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {member?.last_login_at
                        ? new Date(member.last_login_at)?.toLocaleDateString()
                        : 'Never'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {/* Modals */}
      <InviteUserModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        tenantId={profile?.tenant_id}
        onSuccess={handleInviteSuccess}
      />
      <InvitationListModal
        isOpen={isInvitationListOpen}
        onClose={() => setIsInvitationListOpen(false)}
        tenantId={profile?.tenant_id}
      />
    </div>
  );
};

export default TeamTab;